import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useTenant } from "@/context/tenant-context";
import { m365Api } from "@/api/m365";
import type {
  M365Integration,
  GraphSite,
  GraphDrive,
  SharePointConfig,
} from "@/api/m365";
import { queryKeys } from "@/lib/query-keys";
import {
  Link2,
  Unlink,
  Shield,
  MessageSquare,
  Mail,
  FolderOpen,
  Send,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Zap,
  ExternalLink,
  Loader2,
} from "lucide-react";

/* ---------- Constants ---------- */

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  not_configured: "default",
  disconnected: "secondary",
  consent_pending: "warning",
  connected: "success",
  error: "danger",
};

const REQUIRED_PERMISSIONS = [
  { scope: "Team.ReadBasic.All", desc: "List teams" },
  { scope: "Channel.ReadBasic.All", desc: "List channels" },
  { scope: "User.Read.All", desc: "Read user directory" },
  { scope: "Files.ReadWrite.All", desc: "Read/write files" },
  { scope: "Sites.ReadWrite.All", desc: "Access SharePoint sites" },
];

// Mail.* is intentionally NOT requested via admin consent. Per-agent
// mailbox access is granted in the customer tenant via Exchange Online
// RBAC for Applications, configured under each agent's settings.

/* ---------- Advanced: Admin Consent (legacy / tenant-wide capabilities) ---------- */

interface AdvancedAdminConsentProps {
  isConnected: boolean;
  integrationData: M365Integration | null;
  status: string;
  connectMutation: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    mutate: () => void;
  };
  disconnectMutation: { isPending: boolean; mutate: () => void };
  onRefresh: () => void;
}

function AdvancedAdminConsentSection(props: AdvancedAdminConsentProps) {
  const { isConnected, integrationData, status, connectMutation, disconnectMutation, onRefresh } = props;
  const [expanded, setExpanded] = useState(isConnected);

  return (
    <Card>
      <CardHeader>
        <button
          className="flex w-full items-center justify-between text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield size={18} />
            Advanced: Tenant-wide capabilities
          </CardTitle>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <Badge variant="success">connected</Badge>
            ) : status === "consent_pending" ? (
              <Badge variant="warning">awaiting consent</Badge>
            ) : status === "error" ? (
              <Badge variant="danger">error</Badge>
            ) : (
              <Badge variant="default">not connected</Badge>
            )}
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          <p className="text-xs text-text-muted">
            Optional. Required <em>only</em> if you want SharePoint document export, tenant-wide
            Teams channel discovery, or other tenant-scoped capabilities. <strong>Not needed for
            agent email</strong> — the Service Identity above handles all mailbox access.
          </p>

          {isConnected && integrationData ? (
            <div className="flex items-center justify-between rounded-lg bg-surface-hover p-3">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-400" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Admin consent granted</p>
                  <p className="text-xs text-text-muted">
                    Tenant: {integrationData.tenantDomain || "Unknown"}
                    {integrationData.consentedAt && (
                      <> &middot; Consented {new Date(integrationData.consentedAt).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={onRefresh}>
                  <RefreshCw size={14} className="mr-1.5" />
                  Refresh
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={disconnectMutation.isPending}
                  onClick={() => disconnectMutation.mutate()}
                >
                  <Unlink size={14} className="mr-1.5" />
                  Revoke
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
              <p className="text-sm font-medium text-text-primary">Grant tenant-wide admin consent</p>
              <ol className="ml-4 list-decimal space-y-1 text-xs text-text-secondary">
                <li>A Microsoft consent page opens in a new tab.</li>
                <li>Sign in as a <strong>Global Administrator</strong> of the tenant's M365.</li>
                <li>Approve the application permissions (Teams, SharePoint, Users).</li>
                <li>Redirected back here — the tenant-wide capabilities below become available.</li>
              </ol>
              <div className="grid grid-cols-1 gap-1.5 text-[11px] sm:grid-cols-2">
                {REQUIRED_PERMISSIONS.map((p) => (
                  <div key={p.scope} className="flex items-center gap-2 rounded px-2 py-1">
                    <span className="font-mono text-text-primary">{p.scope}</span>
                    <span className="text-text-muted">— {p.desc}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  disabled={connectMutation.isPending}
                  onClick={() => connectMutation.mutate()}
                >
                  <Link2 size={14} className="mr-1.5" />
                  {connectMutation.isPending ? "Starting..." : "Grant admin consent"}
                </Button>
                {status === "consent_pending" && <Badge variant="warning">Awaiting consent</Badge>}
                {status === "error" && <Badge variant="danger">Error — try reconnecting</Badge>}
              </div>
              {connectMutation.isError && (
                <p className="text-xs text-red-500">
                  <AlertCircle size={12} className="mr-1 inline" />
                  {(connectMutation.error as Error).message}
                </p>
              )}
              {status === "consent_pending" && (
                <p className="rounded border border-yellow-500/20 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-400">
                  Waiting for admin consent. Complete the flow in the Microsoft popup, then this page
                  will update automatically.
                </p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/* ---------- Teams Bot ---------- */

function TeamsBotSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();

  const { data: readiness } = useQuery({
    queryKey: ["teams", "readiness"],
    queryFn: () => m365Api.teamsReadiness(),
    staleTime: 60_000,
  });

  const { data: status, isLoading } = useQuery({
    queryKey: ["teams", "install-status", companyId],
    queryFn: () => m365Api.getTeamsInstallStatus(companyId),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.install?.status === "publishing") return 3000;
      return false;
    },
  });

  const installMutation = useMutation({
    mutationFn: () => m365Api.installTeamsBot(companyId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["teams", "install-status", companyId] }),
  });

  const uninstallMutation = useMutation({
    mutationFn: () => m365Api.uninstallTeamsBot(companyId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["teams", "install-status", companyId] }),
  });

  const installStatus = status?.install?.status ?? "not_installed";
  const isPublished = installStatus === "published";
  const isPublishing = installStatus === "publishing";
  const isError = installStatus === "error";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare size={18} />
          Teams Bot — proactive agent ↔ user
          {isPublished && <Badge variant="success">installed</Badge>}
          {isPublishing && <Badge variant="warning">publishing…</Badge>}
          {isError && <Badge variant="danger">error</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-text-muted">
          Lets agents proactively message users on Teams with adaptive cards (approvals,
          notifications, forms) and receive responses back into the agent's workflow. Each agent
          surfaces its identity in the card header — all messages come from the central ProClaw
          bot installed in your tenant.
        </p>

        {!readiness?.configured && (
          <div className="rounded border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-400">
            <AlertCircle size={12} className="mr-1 inline" />
            Platform Teams bot not configured. The platform operator must set
            <span className="font-mono"> MS_TEAMS_BOT_APP_ID</span>,
            <span className="font-mono"> MS_TEAMS_BOT_APP_SECRET</span>,
            <span className="font-mono"> MS_TEAMS_APP_ID</span> on the App Service.
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-text-muted">Loading...</p>
        ) : isPublished ? (
          <>
            <div className="flex items-center justify-between rounded-lg bg-surface-hover p-3">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-400" />
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    Manifest published to your tenant catalog
                  </p>
                  <p className="text-xs text-text-muted">
                    {status?.install?.publishedAt && (
                      <>
                        Published {new Date(status.install.publishedAt).toLocaleString()} ·{" "}
                      </>
                    )}
                    Users can install ProClaw from their Teams app store.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={installMutation.isPending}
                  onClick={() => installMutation.mutate()}
                  title="Re-publish the manifest into your tenant catalog. Use this after a platform update that changes the bot icons, manifest version, or capabilities."
                >
                  Reinstall
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={uninstallMutation.isPending}
                  onClick={() => uninstallMutation.mutate()}
                >
                  <Unlink size={14} className="mr-1.5" />
                  Uninstall
                </Button>
              </div>
            </div>
            {status?.activationDeepLink && (
              <div className="rounded border border-blue-500/20 bg-blue-500/5 px-3 py-2 text-xs text-blue-400">
                <ExternalLink size={12} className="mr-1 inline" />
                Per-user activation link (share with users to one-click install ProClaw to their
                personal scope):{" "}
                <a
                  href={status.activationDeepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono underline"
                >
                  {status.activationDeepLink}
                </a>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
              <p className="text-sm font-medium text-text-primary">Install ProClaw in Teams</p>
              <ol className="ml-4 list-decimal space-y-1 text-xs text-text-secondary">
                <li>
                  Click <strong>Install ProClaw Teams Bot</strong> below. The platform publishes
                  the bot manifest into your tenant's Teams app catalog using your Service
                  Identity OAuth token.
                </li>
                <li>
                  Once published, users in your tenant can install ProClaw to their personal
                  Teams scope from the Teams app store, or click the per-user activation link
                  that appears here after install.
                </li>
                <li>
                  After a user installs, agents in this tenant can proactively send them
                  adaptive cards (approvals, notifications, forms) and receive responses.
                </li>
              </ol>
              {isError && status?.install?.lastError && (
                <p className="rounded bg-red-500/10 px-2 py-1 font-mono text-[11px] text-red-400">
                  {status.install.lastError}
                </p>
              )}
              <Button
                size="sm"
                disabled={
                  !readiness?.configured ||
                  installMutation.isPending ||
                  isPublishing
                }
                onClick={() => installMutation.mutate()}
              >
                {installMutation.isPending || isPublishing ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Link2 size={14} className="mr-1.5" />
                )}
                {isPublishing ? "Publishing…" : "Install ProClaw Teams Bot"}
              </Button>
              {installMutation.isError && (
                <p className="text-xs text-red-500">
                  {(installMutation.error as Error).message}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------- Service Identity (oauth_user mode) ---------- */

function ServiceIdentitySection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [loginHint, setLoginHint] = useState("");

  const { data: identity, isLoading } = useQuery({
    queryKey: ["m365", "oauth-identity", companyId],
    queryFn: () => m365Api.getOAuthIdentity(companyId),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d && d.status === "consent_pending") return 5000;
      return false;
    },
  });

  const startMutation = useMutation({
    mutationFn: () => m365Api.startOAuth(companyId, loginHint || undefined),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["m365", "oauth-identity", companyId] });
      window.open(result.authorizeUrl, "_blank");
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => m365Api.disconnectOAuth(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["m365", "oauth-identity", companyId] });
    },
  });

  const isOAuthMode = identity?.authMode === "oauth_user";
  const isConnected = isOAuthMode && identity?.status === "connected";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail size={18} />
          Service Identity for Agent Email
          {isConnected && <Badge variant="success">connected</Badge>}
          {isOAuthMode && identity?.status === "consent_pending" && (
            <Badge variant="warning">awaiting sign-in</Badge>
          )}
          {isOAuthMode && identity?.status === "error" && (
            <Badge variant="danger">re-auth required</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="text-sm text-text-muted">Loading...</p>
        ) : isConnected ? (
          <>
            <div className="flex items-center justify-between rounded-lg bg-surface-hover p-3">
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-400" />
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    Signed in as <span className="font-mono">{identity?.signedInAs ?? "(unknown)"}</span>
                  </p>
                  <p className="text-xs text-text-muted">
                    Each agent's mailbox access is bounded by what this user has Full Access on.
                    {identity?.tokenExpiresAt && (
                      <> Token refreshes automatically.</>
                    )}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={disconnectMutation.isPending}
                onClick={() => disconnectMutation.mutate()}
              >
                <Unlink size={14} className="mr-1.5" />
                Disconnect
              </Button>
            </div>
            <div className="rounded border border-blue-500/20 bg-blue-500/5 px-3 py-2 text-xs text-blue-400">
              <strong>Adding a new agent mailbox?</strong> In the M365 admin centre, grant{" "}
              <span className="font-mono">{identity?.signedInAs ?? "this user"}</span> Full Access on
              the new shared mailbox, then bind it to the agent in Agent → Config → Mailbox.
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-text-muted">
              Recommended setup. Sign in once as a designated service user
              (e.g. <span className="font-mono">proclaw-svc@your-domain</span>) that has Full Access
              on each shared mailbox an agent will use. ProClaw stores a refresh token and mints
              Graph access tokens as that user with Mail.ReadWrite.Shared / Mail.Send.Shared
              scopes — no tenant-wide mail consent, no PowerShell.
            </p>
            <div className="rounded-lg border border-border bg-surface p-3 space-y-3">
              <p className="text-sm font-medium text-text-primary">Before signing in:</p>
              <ol className="ml-4 list-decimal space-y-1 text-xs text-text-secondary">
                <li>
                  Create (or reuse) a licensed service user in M365, e.g.{" "}
                  <span className="font-mono">proclaw-svc@your-domain</span>.
                </li>
                <li>
                  For each shared mailbox the agent will use, grant this user{" "}
                  <strong>Full Access</strong> (M365 admin centre → Mailbox → Mailbox delegation).
                </li>
                <li>Click <strong>Connect Service Identity</strong> below and sign in <em>as the service user</em>.</li>
              </ol>
              <div className="space-y-2">
                <label className="block text-xs text-text-secondary">
                  Service user UPN (optional — pre-fills the sign-in box)
                </label>
                <Input
                  type="email"
                  placeholder="proclaw-svc@your-domain"
                  value={loginHint}
                  onChange={(e) => setLoginHint(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                disabled={startMutation.isPending}
                onClick={() => startMutation.mutate()}
              >
                <Link2 size={14} className="mr-1.5" />
                {startMutation.isPending ? "Starting..." : "Connect Service Identity"}
              </Button>
              {startMutation.isError && (
                <p className="text-xs text-red-500">
                  {(startMutation.error as Error).message}
                </p>
              )}
              {identity?.status === "consent_pending" && (
                <p className="rounded border border-yellow-500/20 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-400">
                  Sign in pending. Complete the flow in the Microsoft popup, then this page updates automatically.
                </p>
              )}
              {identity?.status === "error" && (
                <p className="rounded border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-400">
                  Last token refresh failed — the customer may have revoked consent or the service user's
                  password changed. Click <strong>Connect Service Identity</strong> to re-authenticate.
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------- Agent Mailbox Summary ---------- */

function AgentMailboxSummary({ companyId }: { companyId: string }) {
  const { data: summary } = useQuery({
    queryKey: ["m365", "mailbox-summary", companyId],
    queryFn: () => m365Api.getMailboxSummary(companyId),
  });
  if (!summary) return null;

  const allVerified = summary.configured > 0 && summary.verified === summary.configured;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield size={18} />
          Per-Agent Mailbox Scope
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-text-muted">
          Each agent in this tenant is bound to one shared mailbox. Mail.* permissions are
          constrained per-mailbox via Exchange Online RBAC for Applications, configured on
          each agent's settings page.
        </p>
        <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
          <div className="rounded border border-border bg-surface p-2">
            <p className="text-text-muted">Agents</p>
            <p className="text-sm font-medium text-text-primary">{summary.totalAgents}</p>
          </div>
          <div className="rounded border border-border bg-surface p-2">
            <p className="text-text-muted">Configured</p>
            <p className="text-sm font-medium text-text-primary">
              {summary.configured} / {summary.totalAgents}
            </p>
          </div>
          <div className="rounded border border-border bg-surface p-2">
            <p className="text-text-muted">Verified</p>
            <p className="text-sm font-medium text-green-400">{summary.verified}</p>
          </div>
          <div className="rounded border border-border bg-surface p-2">
            <p className="text-text-muted">Pending / Failed</p>
            <p className="text-sm font-medium text-yellow-400">
              {summary.pending} / {summary.failed}
            </p>
          </div>
        </div>
        {summary.scopeRequiredFlag ? (
          <p className="rounded border border-green-500/20 bg-green-500/5 px-3 py-2 text-xs text-green-400">
            <CheckCircle size={12} className="mr-1 inline" />
            M365_REQUIRE_MAILBOX_SCOPE=true. Tenant-wide mail access is blocked; only configured
            agent mailboxes can read/send.
          </p>
        ) : !allVerified ? (
          <p className="rounded border border-yellow-500/20 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-400">
            <AlertCircle size={12} className="mr-1 inline" />
            Migration window: agents without a verified mailbox fall back to tenant-wide
            consent. Configure each agent's mailbox under their settings, then ask the platform
            operator to flip M365_REQUIRE_MAILBOX_SCOPE=true.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ---------- Lockdown Panel ---------- */

function LockdownPanel({ companyId }: { companyId: string }) {
  const { data: summary } = useQuery({
    queryKey: ["m365", "mailbox-summary", companyId],
    queryFn: () => m365Api.getMailboxSummary(companyId),
  });
  if (!summary) return null;
  const allVerified = summary.configured > 0 && summary.verified === summary.configured;
  if (!allVerified) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield size={18} />
          Final Lockdown — Revoke tenant-wide Mail.*
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-text-muted">
          Every agent in this tenant has a verified mailbox. You can now revoke the tenant-wide
          Mail.Send / Mail.ReadWrite consent in the customer's Entra portal — the agents will
          continue to work because their access comes from the per-mailbox EXO RBAC role
          assignments, not the broad admin consent.
        </p>
        <ol className="ml-4 list-decimal space-y-1 text-xs text-text-secondary">
          <li>
            Sign in to{" "}
            <a
              href="https://entra.microsoft.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline"
            >
              entra.microsoft.com
            </a>
            {" "}as a Global Administrator of the customer tenant.
          </li>
          <li>
            Go to <strong>Identity</strong> &gt; <strong>Applications</strong> &gt;{" "}
            <strong>Enterprise applications</strong>, search for <em>ProClaw</em>.
          </li>
          <li>
            Open <strong>Permissions</strong>. If <code>Mail.Send</code> or{" "}
            <code>Mail.ReadWrite</code> appear under granted permissions, click <strong>Revoke</strong>.
          </li>
          <li>
            Re-verify each agent in this app to confirm the RBAC scope still works after the
            broad consent is gone.
          </li>
        </ol>
      </CardContent>
    </Card>
  );
}

/* ---------- Teams Webhook Section ---------- */

function TeamsWebhookSection({ companyId }: { companyId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");

  const testMutation = useMutation({
    mutationFn: () =>
      m365Api.testNotification(companyId, {
        channel: "teams_channel",
        webhookUrl,
      }),
  });

  return (
    <Card>
      <CardHeader>
        <button
          className="flex w-full items-center justify-between text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare size={18} />
            Teams Channel Notifications
          </CardTitle>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          <p className="text-xs text-text-muted">
            Send adaptive card notifications to a Teams channel using an incoming webhook. Notifications
            include agent status changes, task completions, and approval requests.
          </p>

          <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
            <p className="text-sm font-medium text-text-primary">Configure incoming webhook</p>
            <ol className="ml-4 list-decimal space-y-1 text-xs text-text-secondary">
              <li>In Microsoft Teams, go to the target channel</li>
              <li>Click <strong>...</strong> &gt; <strong>Connectors</strong> (or <strong>Manage channel</strong> &gt; <strong>Connectors</strong>)</li>
              <li>Find <strong>Incoming Webhook</strong> and click <strong>Configure</strong></li>
              <li>Give it a name (e.g. "ProClaw"), optionally upload an icon, then click <strong>Create</strong></li>
              <li>Copy the webhook URL and paste it below</li>
            </ol>

            <div className="space-y-2 pt-2">
              <label className="block text-xs text-text-secondary">Webhook URL</label>
              <Input
                type="url"
                placeholder="https://xxxxx.webhook.office.com/webhookb2/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
            </div>

            {webhookUrl && (
              <div className="flex items-center gap-2 pt-2">
                <Button size="sm" disabled={testMutation.isPending} onClick={() => testMutation.mutate()}>
                  <Send size={14} className="mr-1.5" />
                  {testMutation.isPending ? "Sending..." : "Send Test Card"}
                </Button>
                {testMutation.isSuccess && (
                  <span className="text-xs text-green-400"><CheckCircle size={12} className="mr-1 inline" />Adaptive card sent to channel</span>
                )}
                {testMutation.isError && (
                  <span className="text-xs text-red-400">{(testMutation.error as Error).message}</span>
                )}
              </div>
            )}
          </div>

          <div className="rounded border border-blue-500/20 bg-blue-500/5 px-3 py-2 text-xs text-blue-400">
            <strong>Why webhooks?</strong> Microsoft Graph API doesn't support sending channel messages
            with application-only tokens. Incoming webhooks are Microsoft's recommended approach for
            app-to-channel notifications and support full adaptive cards.
          </div>
        </CardContent>
      )}
    </Card>
  );
}

/* ---------- Outlook Section ---------- */

function OutlookSection({ companyId }: { companyId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState("");

  const testMutation = useMutation({
    mutationFn: () => m365Api.testNotification(companyId, { channel: "outlook", recipientEmail: email }),
  });

  return (
    <Card>
      <CardHeader>
        <button
          className="flex w-full items-center justify-between text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail size={18} />
            Outlook Email Notifications
          </CardTitle>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          <p className="text-xs text-text-muted">
            Send formatted HTML notification emails via Outlook when agents complete work, change
            status, or require approval. Emails include structured details and links back to ProClaw.
          </p>

          <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
            <p className="text-sm font-medium text-text-primary">Test email notification</p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="recipient@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button size="sm" disabled={testMutation.isPending || !email} onClick={() => testMutation.mutate()}>
                <Send size={14} className="mr-1.5" />
                {testMutation.isPending ? "Sending..." : "Send Test"}
              </Button>
            </div>
            {testMutation.isSuccess && (
              <span className="text-xs text-green-400"><CheckCircle size={12} className="mr-1 inline" />Email sent — check your inbox</span>
            )}
            {testMutation.isError && (
              <span className="text-xs text-red-400">{(testMutation.error as Error).message}</span>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

/* ---------- SharePoint Section ---------- */

function SharePointSection({ companyId }: { companyId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedSite, setSelectedSite] = useState<GraphSite | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<GraphDrive | null>(null);
  const [folderPath, setFolderPath] = useState("/ProClaw/Artifacts");
  const queryClient = useQueryClient();

  const { data: currentConfig } = useQuery({
    queryKey: ["m365", "sharepoint-config", companyId],
    queryFn: () => m365Api.getSharePointConfig(companyId),
    enabled: expanded,
  });

  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ["m365", "graph", "sites", companyId],
    queryFn: () => m365Api.listSites(companyId),
    enabled: expanded,
  });

  const { data: drives, isLoading: drivesLoading } = useQuery({
    queryKey: ["m365", "graph", "drives", companyId, selectedSite?.id],
    queryFn: () => m365Api.listDrives(companyId, selectedSite!.id),
    enabled: !!selectedSite,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      m365Api.updateSharePointConfig(companyId, {
        siteId: selectedSite!.id,
        siteName: selectedSite!.displayName,
        driveId: selectedDrive!.id,
        driveName: selectedDrive!.name,
        folderPath,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["m365", "sharepoint-config", companyId] }),
  });

  return (
    <Card>
      <CardHeader>
        <button
          className="flex w-full items-center justify-between text-left"
          onClick={() => setExpanded(!expanded)}
        >
          <CardTitle className="flex items-center gap-2 text-base">
            <FolderOpen size={18} />
            SharePoint Document Export
          </CardTitle>
          <div className="flex items-center gap-2">
            {currentConfig && typeof currentConfig === "object" && "siteId" in currentConfig ? (
              <Badge variant="success">configured</Badge>
            ) : (
              <Badge variant="default">not configured</Badge>
            )}
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </button>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          <p className="text-xs text-text-muted">
            Export agent artifacts and documents to a SharePoint document library automatically.
          </p>

          <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
            {currentConfig && typeof currentConfig === "object" && "siteId" in currentConfig && (
              <div className="mb-3 rounded bg-green-500/10 px-3 py-2 text-xs text-green-400">
                <CheckCircle size={12} className="mr-1 inline" />
                Exporting to: {(currentConfig as SharePointConfig).siteName} / {(currentConfig as SharePointConfig).driveName} → {(currentConfig as SharePointConfig).folderPath}
              </div>
            )}

            <p className="text-sm font-medium text-text-primary">Configure export destination</p>

            {sitesLoading ? (
              <p className="text-xs text-text-muted">Loading sites...</p>
            ) : (
              <div className="space-y-2">
                <label className="block text-xs text-text-secondary">SharePoint Site</label>
                <select
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary"
                  value={selectedSite?.id ?? ""}
                  onChange={(e) => {
                    setSelectedSite(sites?.find((s) => s.id === e.target.value) ?? null);
                    setSelectedDrive(null);
                  }}
                >
                  <option value="">Select a site...</option>
                  {sites?.map((s) => (
                    <option key={s.id} value={s.id}>{s.displayName}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedSite && (
              <div className="space-y-2">
                <label className="block text-xs text-text-secondary">Document Library</label>
                {drivesLoading ? (
                  <p className="text-xs text-text-muted">Loading document libraries...</p>
                ) : (
                  <select
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary"
                    value={selectedDrive?.id ?? ""}
                    onChange={(e) => setSelectedDrive(drives?.find((d) => d.id === e.target.value) ?? null)}
                  >
                    <option value="">Select a document library...</option>
                    {drives?.map((d) => (
                      <option key={d.id} value={d.id}>{d.name} ({d.driveType})</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {selectedSite && selectedDrive && (
              <>
                <div className="space-y-2">
                  <label className="block text-xs text-text-secondary">Folder Path</label>
                  <Input value={folderPath} onChange={(e) => setFolderPath(e.target.value)} placeholder="/ProClaw/Artifacts" />
                  <p className="text-xs text-text-muted">Folder will be created automatically if it doesn't exist.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                    {saveMutation.isPending ? "Saving..." : "Save SharePoint Configuration"}
                  </Button>
                  {saveMutation.isSuccess && (
                    <span className="text-xs text-green-400"><CheckCircle size={12} className="mr-1 inline" />Saved</span>
                  )}
                </div>
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

/* ---------- Main Page ---------- */

export function M365Settings() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  const { data: platformReady } = useQuery({
    queryKey: ["m365", "readiness"],
    queryFn: () => m365Api.checkReadiness(),
    staleTime: 60_000,
  });

  const { data: integration, isLoading } = useQuery({
    queryKey: queryKeys.m365.byCompany(tenantId),
    queryFn: () => m365Api.getStatus(tenantId),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d && "status" in d && d.status === "consent_pending") return 5000;
      return false;
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.m365.byCompany(tenantId) });

  const connectMutation = useMutation({
    mutationFn: () => m365Api.connect(tenantId),
    onSuccess: (result) => {
      invalidate();
      if ("consentUrl" in result && result.consentUrl) {
        window.open(result.consentUrl, "_blank");
      }
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => m365Api.disconnect(tenantId),
    onSuccess: invalidate,
  });

  const status: string = integration && "status" in integration ? integration.status : "not_configured";
  const isConnected = status === "connected";
  const integrationData =
    isConnected && integration && "id" in integration
      ? (integration as M365Integration)
      : null;

  return (
    <CenterPanel title="Microsoft 365 Integration">
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : (
        <div className="space-y-6">

          {/* Platform not configured — block everything else */}
          {!platformReady?.configured && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircle size={18} className="text-red-400" />
                  Platform Entra app not configured
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-text-muted">
                  Set MS_ENTRA_CLIENT_ID and MS_ENTRA_CLIENT_SECRET on the platform App Service
                  before connecting M365 for any tenant.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Service Identity — primary path. Renders regardless of admin
              consent state. The OAuth flow creates its own integration row. */}
          {platformReady?.configured && <ServiceIdentitySection companyId={tenantId} />}

          {/* Teams Bot — primary path for proactive Teams DMs. Depends on
              Service Identity being connected (uses its OAuth token to
              publish the manifest), so we render once the integration row
              exists. The component itself surfaces the not-configured
              banner if the platform-side bot identity is absent. */}
          {isConnected && <TeamsBotSection companyId={tenantId} />}

          {/* Capabilities that DEPEND on admin consent (tenant-wide) only
              show once the legacy admin-consent integration is connected. */}
          {isConnected && integrationData && (
            <>
              <h2 className="text-lg font-semibold text-text-primary">Capabilities</h2>
              <AgentMailboxSummary companyId={tenantId} />
              <TeamsWebhookSection companyId={tenantId} />
              <OutlookSection companyId={tenantId} />
              <SharePointSection companyId={tenantId} />
              <LockdownPanel companyId={tenantId} />
            </>
          )}

          {/* Advanced: tenant-wide admin consent. Optional — only needed for
              SharePoint export, Teams webhooks at scale, and similar
              tenant-scoped capabilities. Mail-only customers can skip this. */}
          {platformReady?.configured && (
            <AdvancedAdminConsentSection
              isConnected={isConnected}
              integrationData={integrationData}
              status={status}
              connectMutation={connectMutation}
              disconnectMutation={disconnectMutation}
              onRefresh={invalidate}
            />
          )}
        </div>
      )}
    </CenterPanel>
  );
}
